import Loading from "@/common/components/ui/loading/Loading";
import { TableFilters } from "@/common/components/ui/table/TableFilters";
import GenericTable from "@/common/components/ui/table/GenericTable";
import { useInvoiceModals } from "@/common/states/store/slices/invoiceSlice";
import { useAppStore } from "@/common/states/store";
import { ModalCreateInvoice } from "./components/ModalCreateInvoice";
import { CancelInvoiceModal } from "./components/ModalCancellationInvoice";
import { useEmitInvoiceOperation, useGetInvoiceOperation } from "./hook";
import {
  IInvoiceOperation,
  InvoiceOperationStatus,
  InvoiceOperationType,
  STATUS_INVOICE_OPERATION_LABELS,
  STATUS_INVOICE_OPERATION_TYPE,
} from "@/types/invoice-operation.type";
import { format } from "date-fns";
import {  getCompanySigla } from "@/common/helpers/company.helpers";
import TableFooter from "@/common/components/ui/table/TableFooter";
import { formatCurrency } from "@/common/helpers/mask.helpers";

const formatInvoiceList = (invoices: IInvoiceOperation[]) => {
  return (invoices || []).map((invoice) => {
    return {
      ...invoice,
      date: format(new Date(invoice.dateEmission), "dd/MM/yyyy"),
      statusLabel: STATUS_INVOICE_OPERATION_LABELS[invoice.status],
      status: invoice?.status,
      typeLabel:
        STATUS_INVOICE_OPERATION_TYPE[invoice?.type as InvoiceOperationType],
      type: invoice.type,
      companyName: getCompanySigla(invoice.companyId as string),
      companyId: invoice.companyId,
      partnerName: invoice.partner?.companyName,
      partnerId: invoice.partner?._id,
      amount: invoice.amount,
      items: invoice.items,
      dateEmission: format(invoice.dateEmission, "dd/MM/yyyy"),
      natureOperation: invoice.natureOperation,
    };
  });
};

const headers = {
  companyName: "Cidade",
  note: "Observações",
  partnerName: "Cliente",
  dateEmission: "Data de emissão",
  typeLabel: "Tipo",
  statusLabel: "Status",
  amount: "Valor",
};

export const InvoiceTable = () => {
  const { isOpen, cancellationIsOpen } = useInvoiceModals();
  const {
    invoice: {
      filters,
      setFilters,
      setPage,
      page,
      openModalInvoice,
      openModalInvoiceEdit,
      openModalInvoiceCancellation,
    },
  } = useAppStore();
  const { data: invoiceOperations, isLoading } = useGetInvoiceOperation();

  const { mutate: emit } = useEmitInvoiceOperation();

  const handleSearch = (search) => {
    setFilters({ ...search });
  };

  const actions = [
    {
      label: "Editar",
      action: (invoice) => {
        openModalInvoiceEdit(invoice);
      },
      permission: "edit:invoice-operation",
      disabled: (data) => data.status !== InvoiceOperationStatus.IN_PROGRESS,
    },
    {
      label: "Enviar",
      action: (invoice) => emit(invoice._id as string),
      permission: "emit:invoice-operation",
      disabled: (data) => data.status !== InvoiceOperationStatus.IN_PROGRESS,
    },
    {
      label: "Cancelar",
      action: (invoice) => openModalInvoiceCancellation(invoice._id as string),
      permission: "edit:invoice-operation",
      disabled: (data) =>
        data.status !== InvoiceOperationStatus.AUTHORIZED ||
        data.type !== InvoiceOperationType.PRODUCT,
    },
  ];

  return (
    <div className="table-section">
      <TableFilters
        dashboardHeader="Notas Fiscais cadastradas"
        placeholder="Digite o nome do cliente"
        statusOptions={STATUS_INVOICE_OPERATION_LABELS}
        newItemLabel="Nova Nota"
        onNewItemClick={openModalInvoice}
        onSearch={handleSearch}
        entity="invoice-operation"
        // permission="create:invoice-operation"
        tableData={
          invoiceOperations?.data as unknown as Record<string, unknown>[]
        }
      />

      <GenericTable
        headers={headers}
        data={
          formatInvoiceList(
            invoiceOperations?.data as IInvoiceOperation[],
          ) as unknown as Record<string, unknown>[]
        }
        setFilters={() => {
          setFilters({ ...filters });
        }}
        entity="invoice-operation"
        actions={actions}
        renders={{
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          partnerName: (_, invoice) => <p>{(invoice as Record<string, any>)?.customerId?.companyName}</p>,
          amount: (amount) => <p>{formatCurrency(amount)}</p>,
        }}
      />
      <TableFooter
        page={page}
        totalItems={invoiceOperations?.totalItems as number}
        itemsPerPage={10}
        goToPage={setPage}
      />
      {isOpen && <ModalCreateInvoice />}
      {cancellationIsOpen && <CancelInvoiceModal />}

      {isLoading && <Loading />}
    </div>
  );
};
